import React from "react";
import { Link } from "react-router-dom";
import { Camera, LineChart, Globe, Handshake, ArrowRight, Sparkles } from "lucide-react";
import { sellerServices, sellerStats, images } from "../mock";
import PageHero from "../components/PageHero";
import ContactForm from "../components/ContactForm";
import { Button } from "../components/ui/button";
import HomeValueTool from "../components/HomeValueTool";

const icons = [Camera, LineChart, Globe, Handshake];

const Sellers = () => {
  return (
    <div>
      <PageHero
        eyebrow="For Sellers"
        title="Your home deserves a premium debut."
        subtitle="Casual-luxury marketing, sharp pricing, and top-ranking negotiation to net you more."
        image={images.interiors[2]}
      />

      {/* Stats */}
      <section className="bg-[#141210]">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-14 grid grid-cols-1 md:grid-cols-3 gap-8">
          {sellerStats.map((s) => (
            <div key={s.label} className="text-center">
              <div className="font-display text-4xl md:text-5xl text-[#f4ebd9]">{s.value}</div>
              <div className="text-[11px] md:text-xs tracking-luxury uppercase text-[#a8967a] mt-2">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* AI Home Value Tool */}
      <section className="bg-[#f4f1ea]">
        <div className="max-w-5xl mx-auto px-6 md:px-8 py-24">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="inline-flex items-center gap-2 text-xs tracking-luxury uppercase text-gold">
              <Sparkles className="w-4 h-4" /> Instant AI Estimate
            </span>
            <h2 className="font-display text-4xl md:text-5xl text-[#141210] mt-4">
              What's My Home Worth?
            </h2>
            <p className="text-[#5c5348] mt-4">
              Get a free, instant AI value range for your home — then I'll follow up with a precise,
              data-backed CMA prepared just for you.
            </p>
          </div>
          <div className="bg-white rounded-3xl p-8 md:p-11 shadow-[0_30px_70px_-45px_rgba(0,0,0,0.4)] border border-[#efe6d6]">
            <HomeValueTool />
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="max-w-7xl mx-auto px-6 md:px-8 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="text-xs tracking-luxury uppercase text-gold">The Listing Experience</span>
            <h2 className="font-display text-4xl md:text-5xl text-[#141210] mt-4 mb-6 leading-tight">
              Marketed like a magazine. Sold like a pro.
            </h2>
            <p className="text-[#5c5348] leading-relaxed mb-10">
              As a consistent top-ranking listing agent in the Corridor, I treat every home like a
              luxury listing — because presentation and strategy are what turn a "for sale" sign into
              a "sold" sign at top dollar.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {sellerServices.map((s, i) => {
                const Icon = icons[i];
                return (
                  <div key={s.title} className="flex gap-4">
                    <div className="w-11 h-11 rounded-full bg-[#f7edcf] flex items-center justify-center shrink-0">
                      <Icon className="w-5 h-5 text-gold" />
                    </div>
                    <div>
                      <h3 className="font-display text-xl text-[#141210] mb-1">{s.title}</h3>
                      <p className="text-sm text-[#5c5348] leading-relaxed">{s.body}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="img-zoom rounded-3xl overflow-hidden shadow-[0_30px_70px_-40px_rgba(84,64,34,0.5)]">
            <img
              src={images.interiors[3]}
              alt="Beautifully staged luxury interior"
              className="w-full h-[560px] object-cover"
            />
          </div>
        </div>
      </section>

      {/* CTA form */}
      <section className="bg-[#f4f1ea]">
        <div className="max-w-4xl mx-auto px-6 md:px-8 py-24">
          <div className="text-center mb-12">
            <span className="text-xs tracking-luxury uppercase text-gold">Free Home Valuation</span>
            <h2 className="font-display text-4xl md:text-5xl text-[#141210] mt-4">
              What's your home worth today?
            </h2>
            <p className="text-[#5c5348] mt-4 max-w-xl mx-auto">
              Send a few details and I'll prepare a complimentary, no-obligation Corridor market
              analysis just for you.
            </p>
          </div>
          <div className="bg-white rounded-3xl p-8 md:p-11 shadow-[0_30px_70px_-45px_rgba(84,64,34,0.5)] border border-[#efe6d6]">
            <ContactForm type="seller" />
          </div>
          <div className="text-center mt-12">
            <Link to="/about-me">
              <Button variant="outline" className="border-[#d8c9ac] text-[#141210] hover:bg-[#f7edcf] rounded-full px-8 h-12 tracking-luxury uppercase text-xs">
                Meet Your Agent <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Sellers;
